import {
  ClickAwayListener,
  IconButton,
  Popper,
  Stack,
  Grow,
  Switch,
  Paper,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const ExportConfigPanel = ({ open, setOpen }: Props) => {
  const anchorRef = useRef(null);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const [generateTracks, setGenerateTracks] = useState(() =>
    fddmSvc.getPreference('exportSettings.generateTracks'),
  );
  const [generateProximityMatrix, setGenerateProximityMatrix] = useState(() =>
    fddmSvc.getPreference('exportSettings.generateProximityMatrix'),
  );
  const [generateTravelDurationCostMatrix, setGenerateTravelDurationCostMatrix] = useState(() =>
    fddmSvc.getPreference('exportSettings.generateTravelDurationCostMatrix'),
  );

  const onDropDownClick = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const handleSaveDropDownClose = useCallback((event: Event) => {
    // @ts-expect-error strictNullChecks. Pls fix me
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  }, [setOpen]);

  const onGenerateTracksChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, value: boolean) => {
      setGenerateTracks(value);
      fddmSvc.setPreference('exportSettings.generateTracks', value);
    },
    [fddmSvc],
  );

  const onGenerateProximityMatrixChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, value: boolean) => {
      setGenerateProximityMatrix(value);
      fddmSvc.setPreference('exportSettings.generateProximityMatrix', value);
    },
    [fddmSvc],
  );

  const onGenerateTravelDurationCostMatrixChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, value: boolean) => {
      setGenerateTravelDurationCostMatrix(value);
      fddmSvc.setPreference('exportSettings.generateTravelDurationCostMatrix', value);
    },
    [fddmSvc],
  );

  return (
    <>
      <IconButton
        aria-controls={open ? 'split-button-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-label='Settings'
        aria-haspopup='menu'
        onClick={onDropDownClick}
        ref={anchorRef}
        sx={{ padding: 0 }}
      >
        <Settings fontSize='small' />
      </IconButton>
      <Popper
        sx={{
          zIndex: 1,
          minWidth: 100,
        }}
        open={open}
        // pls fix me
        // eslint-disable-next-line react-hooks/refs
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper sx={{ display: 'flex', paddingX: 1, paddingY: 0.5 }}>
              <ClickAwayListener onClickAway={handleSaveDropDownClose}>
                <Stack direction='column' spacing={1}>
                  <PropertiesLabel i18nkey='interface:export.splines' labelRatio={75}>
                    <Switch
                      onChange={onGenerateTracksChange}
                      checked={generateTracks}
                      size='small'
                    />
                  </PropertiesLabel>
                  <PropertiesLabel i18nkey='interface:export.matrix' labelRatio={75}>
                    <Switch
                      onChange={onGenerateProximityMatrixChange}
                      checked={generateProximityMatrix}
                      size='small'
                    />
                  </PropertiesLabel>
                  <PropertiesLabel
                    i18nkey='interface:export.travel_duration_cost_matrix'
                    labelRatio={75}
                  >
                    <Switch
                      onChange={onGenerateTravelDurationCostMatrixChange}
                      checked={generateTravelDurationCostMatrix}
                      size='small'
                    />
                  </PropertiesLabel>
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
